import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  Input,
  Select,
  SelectItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import { supabase } from 'common/src/supabase';
import { Exporter } from 'common/src/utils/types';
import { SEASONS, CURRENT_SEASON } from 'common/src/utils/definitions';
import {
  TrackingSummaryRow,
  fetchTrackingSummary,
} from 'common/src/api/tracking';
import {
  EmptyState,
  KpiCard,
  PageHeader,
  PageLayout,
} from '../../../components/ui';

const defaultSeason = CURRENT_SEASON;

interface SpeciesOption {
  id: number;
  name: string;
}

const ContainersSummaryPage = () => {
  const [season, setSeason] = useState<string>(defaultSeason);
  const [speciesOptions, setSpeciesOptions] = useState<SpeciesOption[]>([]);
  const [selectedSpeciesId, setSelectedSpeciesId] = useState<string | null>(
    null
  );
  const [exporters, setExporters] = useState<Exporter[]>([]);
  const [exportersLoading, setExportersLoading] = useState(true);
  const [selectedExporterId, setSelectedExporterId] = useState<string | null>(
    null
  );
  const [summaryRows, setSummaryRows] = useState<TrackingSummaryRow[]>([]);
  const [totals, setTotals] = useState({
    unique_containers: 0,
    containers_tracked: 0,
  });
  const [loadingSummary, setLoadingSummary] = useState(false);

  useEffect(() => {
    const fetchExporters = async () => {
      setExportersLoading(true);
      const { data, error } = await supabase
        .from('exporters')
        .select('id, name')
        .order('name');
      if (!error && data) {
        setExporters(data as Exporter[]);
      }
      setExportersLoading(false);
    };
    fetchExporters();
  }, []);

  useEffect(() => {
    const fetchSpecies = async () => {
      const { data, error } = await supabase
        .from('species')
        .select('id, name')
        .order('name');
      if (!error && data) {
        setSpeciesOptions(data as SpeciesOption[]);
        if (!selectedSpeciesId && data.length > 0) {
          setSelectedSpeciesId(String(data[0].id));
        }
      }
    };
    fetchSpecies();
  }, [selectedSpeciesId]);

  const getExporterName = useCallback(
    (id: number) => exporters.find((exp) => exp.id === id)?.name || `ID ${id}`,
    [exporters]
  );

  const fetchSummary = useCallback(async () => {
    if (!selectedSpeciesId) {
      return;
    }
    setLoadingSummary(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        toast.error('No hay sesión activa');
        setLoadingSummary(false);
        return;
      }
      const response = await fetchTrackingSummary(session, {
        season,
        speciesId: Number(selectedSpeciesId),
        exporterId: selectedExporterId ? Number(selectedExporterId) : undefined,
      });
      if (!response.success) {
        toast.error(response.error || 'No se pudo obtener el resumen');
      }
      setSummaryRows(response.rows || []);
      setTotals(
        response.totals || {
          unique_containers: 0,
          containers_tracked: 0,
        }
      );
    } catch (error) {
      console.error('Error fetching tracking summary:', error);
      toast.error('Error inesperado al obtener el resumen');
    } finally {
      setLoadingSummary(false);
    }
  }, [season, selectedSpeciesId, selectedExporterId]);

  useEffect(() => {
    if (selectedSpeciesId) {
      fetchSummary();
    }
  }, [fetchSummary, selectedSpeciesId]);

  const filteredRows = useMemo(() => {
    return summaryRows.map((row) => ({
      ...row,
      exporter_name: getExporterName(row.exporter_id),
    }));
  }, [summaryRows, getExporterName]);

  return (
    <PageLayout className='flex flex-col gap-6'>
      <PageHeader
        title='Resumen de Contenedores'
        description='Visualiza cuántos contenedores únicos existen (Shipment Boxes o Frutic) y cuántos ya fueron trackeados, filtrando por temporada, especie y exportadora. Utiliza esta vista para priorizar qué exportadoras necesitan tracking.'
      />

      <Card>
        <CardBody className='grid gap-4'>
          <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-4'>
            <Select
              label='Temporada'
              selectedKeys={[season]}
              onSelectionChange={(keys) =>
                setSeason(Array.from(keys)[0] as string)
              }
            >
              {Object.values(SEASONS).map((s) => (
                <SelectItem key={s.name}>{s.name}</SelectItem>
              ))}
            </Select>

            {exportersLoading ? (
              <Skeleton className='h-14 rounded-lg' />
            ) : (
              <Select
                label='Exportadora'
                selectedKeys={selectedExporterId ? [selectedExporterId] : []}
                onSelectionChange={(keys) => {
                  const value = Array.from(keys)[0] as string | undefined;
                  setSelectedExporterId(value ?? null);
                }}
                placeholder='Todas las exportadoras'
                disallowEmptySelection={false}
              >
                {exporters.map((exp) => (
                  <SelectItem key={exp.id.toString()}>{exp.name}</SelectItem>
                ))}
              </Select>
            )}

            <Select
              label='Especie'
              selectedKeys={selectedSpeciesId ? [selectedSpeciesId] : []}
              onSelectionChange={(keys) => {
                const value = Array.from(keys)[0] as string | undefined;
                setSelectedSpeciesId(value ?? null);
              }}
              placeholder='Selecciona especie'
              disallowEmptySelection={false}
            >
              {speciesOptions.map((spec) => (
                <SelectItem key={spec.id.toString()}>{spec.name}</SelectItem>
              ))}
            </Select>
          </div>

          <div className='flex gap-2 flex-wrap justify-end'>
            <Button
              variant='flat'
              onClick={fetchSummary}
              isDisabled={!selectedSpeciesId}
              isLoading={loadingSummary}
            >
              Actualizar
            </Button>
          </div>
        </CardBody>
      </Card>

      {selectedSpeciesId ? (
        loadingSummary ? (
          <Skeleton className='w-full h-32' />
        ) : (
          <>
            <div className='mb-4 grid grid-cols-2 gap-3 md:grid-cols-3'>
              <KpiCard
                label='Contenedores únicos'
                value={totals.unique_containers.toLocaleString('es-CL')}
              />
              <KpiCard
                label='Contenedores trackeados'
                value={totals.containers_tracked.toLocaleString('es-CL')}
              />
              <KpiCard
                label='Porcentaje trackeado'
                value={
                  totals.unique_containers > 0
                    ? `${(
                        (totals.containers_tracked / totals.unique_containers) *
                        100
                      ).toFixed(2)}%`
                    : '—'
                }
              />
            </div>
            <Card shadow='sm'>
              <CardBody className='overflow-x-auto'>
                {filteredRows.length === 0 ? (
                  <EmptyState title='No hay contenedores para los filtros seleccionados.' />
                ) : (
                  <Table removeWrapper aria-label='Resumen contenedores'>
                    <TableHeader>
                      <TableColumn>EXPORTADORA</TableColumn>
                      <TableColumn>UNIQUE CONTAINERS</TableColumn>
                      <TableColumn>CONTAINERS TRACKED</TableColumn>
                      <TableColumn>PERCENT</TableColumn>
                    </TableHeader>
                    <TableBody>
                      {filteredRows.map((row: TrackingSummaryRow) => (
                        <TableRow key={row.exporter_id.toString()}>
                          <TableCell>{row.exporter_name}</TableCell>
                          <TableCell>
                            {row.unique_containers.toLocaleString('es-CL')}
                          </TableCell>
                          <TableCell>
                            {row.containers_tracked.toLocaleString('es-CL')}
                          </TableCell>
                          <TableCell>
                            {row.unique_containers > 0
                              ? (
                                  (row.containers_tracked /
                                    row.unique_containers) *
                                  100
                                ).toFixed(2)
                              : '0.00'}
                            %
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell className='font-semibold'>TOTAL</TableCell>
                        <TableCell className='font-semibold'>
                          {totals.unique_containers.toLocaleString('es-CL')}
                        </TableCell>
                        <TableCell className='font-semibold'>
                          {totals.containers_tracked.toLocaleString('es-CL')}
                        </TableCell>
                        <TableCell className='font-semibold'>
                          {(
                            (totals.containers_tracked /
                              totals.unique_containers || 1) * 100
                          ).toFixed(2)}
                          %
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </>
        )
      ) : (
        <EmptyState title='Selecciona una especie para ver el resumen.' />
      )}
    </PageLayout>
  );
};

export default ContainersSummaryPage;
